import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, Button, Alert, Grid,
  InputAdornment, Chip, Divider,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';

export default function ReferralCapture() {
  const [code, setCode] = useState('');
  const [validated, setValidated] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(window.location.search);
  const refFromUrl = urlParams.get('ref') || '';

  const validateCode = () => {
    setError('');
    setValidated(null);
    if (!code && !refFromUrl) {
      setError('Please enter a referral code');
      return;
    }
    const c = code || refFromUrl;
    // Mock validation
    if (/^\d+$/.test(c)) {
      setValidated({ type: 'User Referral', owner: 'Ahmad bin Abdullah', code: c });
    } else if (/^[A-Z]+$/i.test(c)) {
      setValidated({ type: 'Agent Referral', owner: 'CitiPro Financial Services', code: c });
    } else {
      setValidated({ type: 'Campaign', owner: 'iMoney Q2 2026 Campaign', code: c });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F5F7FA', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Box sx={{ maxWidth: 600, width: '100%' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1B365D', mb: 1 }}>
            iMoney
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Enter your referral code to get started
          </Typography>
        </Box>

        {refFromUrl && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            Referral code <strong>{refFromUrl}</strong> detected from your link. Click validate to continue.
          </Alert>
        )}

        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
              Referral Code
            </Typography>

            <TextField
              fullWidth
              value={code || refFromUrl}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter referral code, phone number, or agent code"
              InputProps={{
                startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#90A4AE' }} /></InputAdornment>,
              }}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            <Button
              fullWidth variant="contained" onClick={validateCode}
              sx={{ borderRadius: 2, py: 1.5, textTransform: 'none', fontWeight: 600, backgroundColor: '#1B365D', mb: 2 }}
            >
              Validate Code
            </Button>

            {validated && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: 2, mb: 2 }}>
                  Referral code is valid!
                </Alert>
                <Box sx={{ backgroundColor: '#F5F7FA', borderRadius: 2, p: 2.5 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Code</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{validated.code}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Type</Typography>
                      <Chip label={validated.type} size="small" sx={{ fontWeight: 600, backgroundColor: '#E3F2FD', color: '#1565C0' }} />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Referred By</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{validated.owner}</Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Button
                  fullWidth variant="contained" color="secondary"
                  onClick={() => navigate(`/apply?ref=${validated.code}`)}
                  sx={{ mt: 2, borderRadius: 2, py: 1.5, textTransform: 'none', fontWeight: 600 }}
                >
                  Continue to Application
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" color="text.secondary">or</Typography>
            </Divider>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  fullWidth variant="outlined" startIcon={<QrCodeScannerIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none', py: 1.5 }}
                >
                  Scan QR Code
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth variant="outlined" startIcon={<LinkIcon />}
                  onClick={() => navigate('/products')}
                  sx={{ borderRadius: 2, textTransform: 'none', py: 1.5 }}
                >
                  Browse Products
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Don't have a referral code?{' '}
            <Button size="small" sx={{ textTransform: 'none', fontWeight: 600 }} onClick={() => navigate('/products')}>
              Apply directly
            </Button>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
